import { Package, TrendingUp, Tag, DollarSign, Layers, BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface Product {
  id: string;
  name: string;
  base_price: number;
  is_active: boolean;
  category: string | null;
  tier: string | null;
}

interface PricingRule {
  id: string;
  is_active: boolean;
  product_id: string | null;
}

interface ProductStatsProps {
  products: Product[] | undefined;
  pricingRules: PricingRule[] | undefined;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

export function ProductStats({ products, pricingRules }: ProductStatsProps) {
  const activeProducts = products?.filter((p) => p.is_active).length || 0;
  const totalProducts = products?.length || 0;
  const totalValue = products?.reduce((sum, p) => sum + p.base_price, 0) || 0;
  const avgPrice = totalProducts > 0 ? totalValue / totalProducts : 0;
  const activeRules = pricingRules?.filter((r) => r.is_active).length || 0;
  const categories = new Set(products?.map((p) => p.category).filter(Boolean)).size;
  const tiers = new Set(products?.map((p) => p.tier).filter(Boolean)).size;

  const stats = [
    {
      title: "Active Products",
      value: activeProducts,
      subtitle: `of ${totalProducts} total`,
      icon: Package,
      gradient: "from-primary/10 to-primary/5",
      borderColor: "border-primary/20",
      iconColor: "text-primary",
    },
    {
      title: "Catalog Value",
      value: formatCurrency(totalValue),
      subtitle: "total base prices",
      icon: TrendingUp,
      gradient: "from-cyan-500/10 to-cyan-500/5",
      borderColor: "border-cyan-500/20",
      iconColor: "text-cyan-500",
    },
    {
      title: "Average Price",
      value: formatCurrency(avgPrice),
      subtitle: "per product",
      icon: DollarSign,
      gradient: "from-green-500/10 to-green-500/5",
      borderColor: "border-green-500/20",
      iconColor: "text-green-500",
    },
    {
      title: "Categories",
      value: categories,
      subtitle: "product categories",
      icon: Layers,
      gradient: "from-purple-500/10 to-purple-500/5",
      borderColor: "border-purple-500/20",
      iconColor: "text-purple-500",
    },
    {
      title: "Pricing Tiers",
      value: tiers,
      subtitle: "price tiers",
      icon: BarChart3,
      gradient: "from-orange-500/10 to-orange-500/5",
      borderColor: "border-orange-500/20",
      iconColor: "text-orange-500",
    },
    {
      title: "Pricing Rules",
      value: activeRules,
      subtitle: "active rules",
      icon: Tag,
      gradient: "from-amber-500/10 to-amber-500/5",
      borderColor: "border-amber-500/20",
      iconColor: "text-amber-500",
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {stats.map((stat) => (
        <Card
          key={stat.title}
          className={`bg-gradient-to-br ${stat.gradient} ${stat.borderColor}`}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
            <stat.icon className={`h-4 w-4 ${stat.iconColor}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
